// pages/admin/AdminActivityLogs.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import AdminPagination from './components/AdminPagination';

const AdminActivityLogs = () => {
  const { apiCall, addNotification } = useAdmin();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const limit = 20;

  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  const debouncedSearch = useCallback(
    debounce((term) => {
      setCurrentPage(1);
      loadActivityLogs(1, term, typeFilter, dateFrom, dateTo);
    }, 500),
    [typeFilter, dateFrom, dateTo]
  );

  useEffect(() => {
    loadActivityLogs();
  }, [currentPage, typeFilter, dateFrom, dateTo]);

  useEffect(() => {
    debouncedSearch(searchTerm);
  }, [searchTerm, debouncedSearch]);

  const loadActivityLogs = async (page = currentPage, search = searchTerm, type = typeFilter, startDate = dateFrom, endDate = dateTo) => {
    try {
      setLoading(true);
      
      const queryParams = new URLSearchParams({
        page,
        limit,
        ...(search && { search }),
        ...(type && { type }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate })
      });

      const response = await apiCall(
        `https://phenixloc-api.fnvt.net/api/admin/activity-logs?${queryParams.toString()}`
      );

      setLogs(response.logs);
      setCurrentPage(response.pagination.page);
      setTotalPages(response.pagination.totalPages);
      setTotalLogs(response.pagination.total);

    } catch (error) {
      console.error('Error loading activity logs:', error);
      addNotification('Error loading activity logs', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatActionTitle = (action) => {
    const words = action.split('_');
    return words.map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
  };

  const getActivityIcon = (action) => {
    if (action.includes('USER_')) {
      if (action.includes('CREATE')) return 'fa-user-plus';
      if (action.includes('DELETE')) return 'fa-user-minus';
      if (action.includes('BLOCK')) return 'fa-user-lock';
      return 'fa-user-edit';
    }
    
    if (action.includes('INHERITANCE_')) {
      if (action.includes('CREATE')) return 'fa-file-alt';
      if (action.includes('DELETE')) return 'fa-file-excel';
      if (action.includes('DELIVER')) return 'fa-paper-plane';
      return 'fa-file-alt';
    }
    
    if (action.includes('LOGIN')) return 'fa-sign-in-alt';
    if (action.includes('LOGOUT')) return 'fa-sign-out-alt';
    if (action.includes('VIEW')) return 'fa-eye';
    if (action.includes('SETTINGS')) return 'fa-cog';
    
    return 'fa-clipboard-list';
  };

  const getIconClass = (action) => {
    if (action.includes('USER')) return 'user-icon';
    if (action.includes('INHERITANCE')) return 'inheritance-icon';
    if (action.includes('DELIVER')) return 'delivery-icon';
    return 'admin-icon';
  };

  const groupLogsByDate = (logs) => {
    const groups = {};
    
    logs.forEach(log => {
      const logDate = new Date(log.createdAt);
      const dateKey = formatDateKey(logDate);
      
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      
      groups[dateKey].push(log);
    });
    
    return groups;
  };

  const formatDateKey = (date) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const normalizedToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const normalizedYesterday = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
    
    if (normalizedDate.getTime() === normalizedToday.getTime()) {
      return 'Today';
    } else if (normalizedDate.getTime() === normalizedYesterday.getTime()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
    }
  };

  const renderActivityTimeline = () => {
    if (logs.length === 0) {
      return (
        <div className="admin-empty-data">
          <p>No activity logs found. Try adjusting your filters.</p>
        </div>
      );
    }

    const groupedLogs = groupLogsByDate(logs);
    
    return Object.keys(groupedLogs).map(date => (
      <div key={date} className="admin-timeline-group">
        <div className="admin-timeline-date">{date}</div>
        {groupedLogs[date].map(log => {
          const logTime = new Date(log.createdAt);
          const formattedTime = logTime.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit'
          });

          return (
            <div key={log.id} className="admin-timeline-item">
              <div className={`admin-timeline-icon ${getIconClass(log.action)}`}>
                <i className={`fas ${getActivityIcon(log.action)}`}></i>
              </div>
              <div className="admin-timeline-content">
                <div className="admin-timeline-header">
                  <h4>{formatActionTitle(log.action)}</h4>
                  <span className="admin-timeline-time">{formattedTime}</span>
                </div>
                <div className="admin-timeline-body">
                  <p>{log.description}</p>
                  <div className="admin-timeline-details">
                    <span className="admin-timeline-detail">
                      <i className="fas fa-user-shield"></i> 
                      {log.adminUsername || 'System'}
                    </span>
                    <span className="admin-timeline-detail">
                      <i className="fas fa-fingerprint"></i> 
                      IP: {log.ipAddress || 'Unknown'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    ));
  };

  return (
    <div className="admin-activity-logs">
      <div className="admin-page-header">
        <h2>Activity Logs</h2>
        <p>System-wide activity and audit trail</p>
      </div>

      <div className="admin-filter-bar">
        <div className="admin-search-filter">
          <input
            type="text"
            placeholder="Search activities..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <i className="fas fa-search"></i>
        </div>
        <div className="admin-action-filters">
          <select 
            value={typeFilter} 
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">Activity Type: All</option>
            <option value="user">User Actions</option>
            <option value="inheritance">Inheritance Actions</option>
            <option value="admin">Admin Actions</option>
            <option value="system">System Events</option>
          </select>
          <div className="admin-date-range-filter">
            <input
              type="date"
              className="admin-date-input"
              placeholder="From"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
            <span>to</span>
            <input
              type="date"
              className="admin-date-input"
              placeholder="To"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="admin-activity-timeline">
        {loading ? (
          <div className="admin-loading-data">
            <p>Loading activity logs...</p>
          </div>
        ) : (
          renderActivityTimeline()
        )}
      </div>

      <AdminPagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </div>
  );
};

export default AdminActivityLogs;